import { Button, Flex, Select, TextInput } from "@mantine/core";
import { SelectItem } from "@mantine/core/lib/Select/types";
import { map, sortBy } from "lodash";
import isNil from "lodash/isNil";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover";
import Spoiler from "../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../components/Table/types";
import { UpdateDeskCollectionInput } from "../../../graphql/generated.ts";
import { DeskCollection } from "../../../types/deskCollection/deskCollection.ts";
import { toLocalDate } from "../../../utils/date.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { DeskCollectionLogs } from "../DeskCollectionLogs.tsx";
import { generateDeskCollectionMessage } from "../utils.ts";

interface Props {
  data: DeskCollection[];
  templates: SelectItem[];
  options?: { inlineEdit: Record<string, boolean> };
  onInlineEdit: (
    item: DeskCollection,
    value: UpdateDeskCollectionInput
  ) => void;
  onSend: (id: number, message: string) => Promise<void>;
}

export function useDeskCollectionTableData({
  data,
  templates,
  options,
  onInlineEdit,
  onSend,
}: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "customerName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "picName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "picName",
          }}
        >
          PIC Name
        </DatabaseMetadataPopover>
      ),
      width: "250px",
    },
    {
      accessor: "phone",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "phone",
          }}
        >
          Phone Number
        </DatabaseMetadataPopover>
      ),
      width: "200px",
    },
    {
      accessor: "invoiceNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceNumber",
          }}
        >
          Invoice Number
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
    {
      accessor: "invoiceCount",
      title: "Invoice Count",
    },
    {
      accessor: "invoiceDueDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceDueDate",
          }}
        >
          Invoice Due Date
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
    {
      accessor: "dpd",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "dpd",
          }}
        >
          Invoice Due Date - Today
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "totalInvoice",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalInvoice",
          }}
        >
          Total Invoice Amount
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
    {
      accessor: "totalAccountReceivable",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalAccountReceivable",
          }}
        >
          Total AR
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
    {
      accessor: "template",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "template",
          }}
        >
          Message Template
        </DatabaseMetadataPopover>
      ),
      width: "200px",
    },
    {
      accessor: "message",
      width: "350px",
    },
    {
      accessor: "logs",
      width: "300px",
    },
    {
      accessor: "actions",
      title: "",
    },
  ];

  const sendMessage = useCallback(
    (item: (typeof data)[0], message: string | null) => {
      let whatsAppLink = `https://api.whatsapp.com/send?phone=${item.phone}`;
      if (message) {
        whatsAppLink = `${whatsAppLink}&text=${encodeURIComponent(message)}`;
      }
      onSend(item.id, message ?? "").then(() => {
        window.open(whatsAppLink, "_blank", "noopener,noreferrer");
      });
    },
    [onSend]
  );

  const rows = useMemo(() => {
    return data?.map((item, index) => {
      const message = generateDeskCollectionMessage(item);
      const invoices = sortBy(item.invoices, "dpd");
      return {
        id: index,
        customerName: item.customer.name,
        picName: options?.inlineEdit.picName ? (
          <TextInput
            key={`picName-${item.id}-${item.picName}`}
            defaultValue={item.picName ?? undefined}
            size="s"
            placeholder="Enter PIC Name"
            onBlur={(event) =>
              onInlineEdit(item, { picName: event.target.value })
            }
          />
        ) : (
          item.picName
        ),
        phone: options?.inlineEdit.phone ? (
          <TextInput
            key={`phone-${item.id}-${item.phone}`}
            defaultValue={item.phone ?? undefined}
            size="s"
            placeholder="Enter Phone"
            onBlur={(event) =>
              onInlineEdit(item, { phone: event.target.value })
            }
          />
        ) : (
          item.phone
        ),
        invoiceNumber: map(invoices, "invoiceNumber").join("; "),
        invoiceCount: invoices.length,
        invoiceDueDate: map(invoices, (i) =>
          toLocalDate(i.invoiceDueDate, "DD/MM/YY")
        ).join("; "),
        dpd: map(invoices, (i) => (isNil(i.dpd) ? "None" : i.dpd)).join("; "),
        totalInvoice: map(invoices, (i) =>
          toCurrencyPrice(i.totalInvoice, "Rp")
        ).join("; "),
        totalAccountReceivable: map(invoices, (i) =>
          toCurrencyPrice(i.totalAccountReceivable, "Rp")
        ).join("; "),
        template: (
          <Select
            key={item.id}
            data={templates}
            value={item.template ? String(item.template.id) : null}
            placeholder="Select Template"
            size="s"
            withinPortal
            zIndex={100}
            onChange={(value) =>
              onInlineEdit(item, { templateId: Number(value) })
            }
            onClick={(event) => event.stopPropagation()}
          />
        ),
        message: message ? (
          <Spoiler
            style={{ whiteSpace: "pre-line" }}
            maxStringLength={150}
            text={message}
          />
        ) : undefined,
        logs: <DeskCollectionLogs logs={item.logs} />,
        actions: (
          <Flex gap={10}>
            <Button
              variant="outlineGolden"
              size="xs"
              disabled={!item.phone}
              onClick={() => sendMessage(item, null)}
            >
              Send Empty Message
            </Button>
            <Button
              variant="filledGolden"
              size="xs"
              disabled={!item.phone || !message}
              onClick={() => sendMessage(item, message)}
            >
              Send Message
            </Button>
          </Flex>
        ),
      };
    });
  }, [data, onInlineEdit, options?.inlineEdit, sendMessage, templates]);

  return [columns, rows];
}
